import { IAlarm } from "../Models/IAlarm";
import { IRole } from "../Models/IRole";
import { ISentinelRich } from "../Models/ISentinel";
import { IUser } from "../Models/IUser";
import { getSensorIconTooltip } from "./SensorMaps";

// Testi della Home e della finestra dell'allarme: i nomi dei sensori sono quelli di sempre (SensorMaps),
// scelti dal titolare; qui solo l'ordine e qualche etichetta.

// ordine di importanza quando un dispositivo ha piu' allarmi aperti
const PRIORITY = ["pesogr", "lidarSx", "lidarDx", "rumore", "door", "pesogr2"];

export const sensorName = (type: string) => getSensorIconTooltip(type);

export const alarmType = (alarm: IAlarm) => alarm.sensor?.type ?? alarm.sensorType;

// tipi di sensore in allarme, dal piu' importante, senza doppioni
export const alarmTypesByPriority = (alarms: IAlarm[]): string[] => {
  const types = Array.from(new Set(alarms.map(alarmType)));
  const rank = (t: string) => (PRIORITY.indexOf(t) === -1 ? 99 : PRIORITY.indexOf(t));
  return types.sort((a, b) => rank(a) - rank(b));
};

export const isGatewaySentinel = (sentinel: { identifier: string }) =>
  (sentinel.identifier ?? "").toLowerCase().includes("varco");

// un campo lasciato vuoto nel Setup arriva come "", "-", "0", "0-": non e' un dato
const isBlank = (v: string | undefined | null) => /^[\s0-]*$/.test(String(v ?? ""));

// la stanza vuota non si mostra
export const roomLabel = (room: string | undefined | null) =>
  isBlank(room) ? "" : String(room).trim().padStart(2, "0");

// nome e zona non dati: un testo al posto del trattino
export const nameLabel = (name: string | undefined | null) => (isBlank(name) ? "Senza nome" : String(name).trim());
export const zoneLabel = (floor: string | undefined | null) => (isBlank(floor) ? "Senza zona" : String(floor).trim());

// «Letto · Primo piano · Camera»
export const sentinelWhere = (sentinel: ISentinelRich) =>
  [
    isGatewaySentinel(sentinel) ? "Porta" : "Letto",
    isBlank(sentinel.patient?.floor) ? "" : String(sentinel.patient?.floor).trim(),
    roomLabel(sentinel.patient?.room),
  ]
    .filter((x: string) => x !== "")
    .join(" · ");

export const hhmm = (date: string | number | Date) =>
  new Date(date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

// ora, con la data davanti se non e' oggi
export const dayAndTime = (date: string) => {
  const d = new Date(date);
  const today = d.toDateString() === new Date().toDateString();
  return (today ? "" : d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) + " ") + hhmm(d);
};

// «da 25 min», «da 3 h», «da 2 giorni»
export const sinceLabel = (date: string) => {
  const m = Math.max(1, Math.round((Date.now() - new Date(date).getTime()) / 60000));
  if (m < 60) return m + " min";
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? "1 ora" : h + " ore";
  const g = Math.floor(h / 24);
  return g === 1 ? "1 giorno" : g + " giorni";
};

// chi puo' riaccendere un dispositivo dal Setup
export const canUseSetup = (user: IUser | undefined) =>
  (user?.roles ?? []).some((x: IRole) => x.type === "superAdmin" || x.type === "installer");
